/**
 * Utility functions for user data handling
 * Updated: Profile management support
 */

import { isSyntheticInfluencerEmail } from './influencerInviteEmail'

/**
 * Get user's full name from first_name and last_name, with fallback to full_name
 * @param {Object} user - User object
 * @returns {string} Full name or real email or 'Unknown User'
 */
export const getUserFullName = (user) => {
  if (!user) return 'Unknown User'

  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  if (user.first_name) return user.first_name
  if (user.last_name) return user.last_name
  if (user.full_name) return user.full_name // backward compatibility
  const email = user.email != null ? String(user.email).trim() : ''
  if (email && !isSyntheticInfluencerEmail(email)) return email
  return 'Unknown User'
}

/**
 * Get user's initials for avatar display
 * @param {Object} user - User object
 * @returns {string} Initials (e.g., "JD" for John Doe)
 */
export const getUserInitials = (user) => {
  if (!user) return '?'

  const first = user.first_name?.[0]?.toUpperCase() || ''
  const last = user.last_name?.[0]?.toUpperCase() || ''
  
  if (first && last) return `${first}${last}`
  if (first) return first
  if (user.full_name) {
    // Fallback: use first letter of each word in full_name
    const words = user.full_name.trim().split(/\s+/)
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
    }
    if (words.length === 1) {
      return words[0][0].toUpperCase()
    }
  }
  const em = user.email != null ? String(user.email).trim() : ''
  if (em && !isSyntheticInfluencerEmail(em) && em[0]) return em[0].toUpperCase()
  return '?'
}

/**
 * Get gradient color for avatar based on user initials or email
 * @param {Object} user - User object
 * @returns {string} CSS gradient string
 */
export const getAvatarGradient = (user) => {
  if (!user) {
    // Brand fallback: deep periwinkle → deep wisteria (AA-safe under white initials).
    return 'linear-gradient(135deg, #545c8f 0%, #7e5d94 100%)'
  }

  // Use initials or email to generate consistent color
  const seed = getUserInitials(user) || user.email || '?'
  const hash = seed.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)

  // Generate colors from hash
  const hue = Math.abs(hash) % 360
  const saturation = 60 + (Math.abs(hash) % 20) // 60-80%
  const lightness = 50 + (Math.abs(hash) % 15) // 50-65%

  return `linear-gradient(135deg, hsl(${hue}, ${saturation}%, ${lightness}%) 0%, hsl(${(hue + 30) % 360}, ${saturation}%, ${lightness - 10}%) 100%)`
}

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL
 */
export const isValidUrl = (url) => {
  if (!url) return true // Empty is valid (optional field)
  try {
    new URL(url.startsWith('http') ? url : `https://${url}`)
    return true
  } catch {
    return false
  }
}

/**
 * Format social media handle (add @ if missing, remove @ if duplicate)
 * @param {string} handle - Social media handle
 * @returns {string} Formatted handle
 */
export const formatSocialHandle = (handle) => {
  if (!handle) return ''
  const trimmed = handle.trim()
  if (trimmed.startsWith('@')) {
    return trimmed
  }
  return `@${trimmed}`
}
